import { useState, useRef, useEffect } from "react";
import {
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from "react-resizable-panels";
import { HistoryPanel } from "@/components/HistoryPanel";
import { ChatPanel } from "@/components/ChatPanel";
import { DashboardViewport } from "@/components/DashboardViewport";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { LiveSignalsPanel } from "@/components/LiveSignalsPanel";
import { MobileShell } from "@/components/MobileShell";
import { useApp } from "@/context/AppCtx";
import { useAutoPilot } from "@/hooks/useAutoPilot";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useStrategist } from "@/hooks/useStrategist";
import { ChevronLeft, ChevronRight, Zap, Target } from "lucide-react";
import { TokenAvatar } from "@/components/TokenAvatar";
import { useTranslation } from "@/lib/i18n/useTranslation";

// ── Bundled live-signal toast ────────────────────────────────────────────────
// New signals that arrive within a 1.5 s window are coalesced into one toast
// instead of firing individual popups for every strategy hit.
interface SignalBundleToast {
  id: string;
  count: number;
  firstLabel: string;
  firstCoin: string;
  tokens: { symbol: string; mint: string; imageUri?: string | null }[];
}

function LiveSignalToasts() {
  const { recommendations } = useStrategist();
  const [toasts, setToasts] = useState<SignalBundleToast[]>([]);
  const seenIdsRef = useRef(new Set<string>());
  const pendingRef = useRef<{ label: string; coin: string; mint: string; imageUri?: string | null }[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const DEBOUNCE_MS = 1500;
  const DISPLAY_MS  = 5000;

  const flush = () => {
    const batch = pendingRef.current.splice(0);
    if (batch.length === 0) return;
    const id = `sig-${Date.now()}`;
    const toast: SignalBundleToast = {
      id,
      count: batch.length,
      firstLabel: batch[0].label,
      firstCoin: batch[0].coin,
      tokens: batch.map(b => ({
        symbol: b.coin,
        mint: b.mint,
        imageUri: b.imageUri,
      })),
    };
    setToasts(prev => [...prev, toast]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), DISPLAY_MS);
  };

  useEffect(() => {
    // Seed on mount — don't toast existing signals
    if (seenIdsRef.current.size === 0) {
      recommendations.forEach(r => seenIdsRef.current.add(r.id));
      return;
    }

    const fresh = recommendations.filter(r => !seenIdsRef.current.has(r.id));
    if (fresh.length === 0) return;

    fresh.forEach(r => {
      seenIdsRef.current.add(r.id);
      pendingRef.current.push({ label: r.label, coin: r.coin, mint: r.mint, imageUri: r.imageUri });
    });

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(flush, DEBOUNCE_MS);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendations]);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[300] flex flex-col gap-2 pointer-events-none w-[90%] max-w-[360px]">
      {toasts.map(t => (
        <div
          key={t.id}
          className="animate-in slide-in-from-bottom fade-in duration-400 flex items-center gap-3 bg-[#0b0e11]/95 backdrop-blur-md border border-amber-500/30 shadow-[0_4px_24px_rgba(245,158,11,0.25)] rounded-full pl-1.5 pr-5 py-1.5 pointer-events-auto"
        >
          {/* Avatar Facepile — multiple overlapping tokens */}
          <div className="relative flex items-center -space-x-3.5 shrink-0">
            {t.tokens.slice(0, 8).map((tok, i) => (
              <div 
                key={tok.mint || i} 
                className="relative size-11 rounded-full overflow-hidden border-2 border-[#0b0e11] shadow-[0_0_10px_rgba(245,158,11,0.3)] animate-in zoom-in spin-in-12 duration-500 shrink-0"
                style={{ zIndex: 30 - i, animationDelay: `${i * 80}ms` }}
              >
                <TokenAvatar
                  mint={tok.mint}
                  symbol={tok.symbol}
                  imageUri={tok.imageUri}
                  size="size-11"
                  animated={false}
                />
              </div>
            ))}
            {t.tokens.length === 0 && (
              <div className="relative size-11 rounded-full bg-amber-500/15 border border-amber-500/30 shrink-0 flex items-center justify-center shadow-[0_0_10px_rgba(245,158,11,0.3)]">
                <Zap className="size-5 text-amber-400" fill="currentColor" />
              </div>
            )}
            {t.count > 8 && (
              <div className="relative size-11 rounded-full overflow-hidden bg-black/80 border-2 border-[#0b0e11] z-0 flex items-center justify-center shadow-[0_0_10px_rgba(245,158,11,0.3)]">
                <span className="text-xs font-black text-white">+{t.count - 8}</span>
              </div>
            )}
          </div>

          <div className="flex flex-col min-w-0 flex-1">
            <span className="text-[12px] font-black text-white leading-tight truncate" title={t.tokens.map(tk => tk.symbol).join(", ")}>
              {t.count === 1 ? t.firstCoin : `${t.tokens.slice(0, 4).map(tk => tk.symbol).join(", ")}${t.count > 4 ? ` +${t.count - 4} more` : ""}`}
            </span>
            <span className="text-[10px] font-bold text-amber-400 flex items-center gap-1 truncate">
              <Zap className="size-3 shrink-0" fill="currentColor" />
              <span className="truncate">{t.count === 1 ? t.firstLabel : `${t.count} live signals detected`}</span>
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function ResizeHandle({ className, direction = "horizontal", onCollapse, onExpand }: { className?: string, direction?: "horizontal" | "vertical", onCollapse?: () => void, onExpand?: () => void }) {
  return (
    <PanelResizeHandle className={`group relative flex items-center justify-center bg-[var(--color-border)] hover:bg-[var(--color-fg-dim)] transition-colors duration-150 ${className || ""} ${direction === "horizontal" ? "w-px h-full cursor-col-resize" : "h-px w-full cursor-row-resize"}`}>
      <span className="absolute inset-0" aria-hidden />
      {(onCollapse || onExpand) && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onCollapse ? onCollapse() : onExpand?.();
          }}
          className={`absolute z-10 flex items-center justify-center rounded-full bg-[var(--color-bg-editor)] border border-[var(--color-border)] hover:border-[var(--color-fg-dim)] hover:bg-[var(--color-fill)] transition-all ${direction === "horizontal" ? "w-6 h-6" : "h-6 w-6"}`}
          title={onCollapse ? "Collapse panel" : "Expand panel"}
        >
          {direction === "horizontal" ? (
            onCollapse ? <ChevronLeft className="size-3 text-[var(--color-fg-dim)]" /> : <ChevronRight className="size-3 text-[var(--color-fg-dim)]" />
          ) : (
            onCollapse ? <ChevronLeft className="size-3 text-[var(--color-fg-dim)] rotate-90" /> : <ChevronRight className="size-3 text-[var(--color-fg-dim)] rotate-90" />
          )}
        </button>
      )}
    </PanelResizeHandle>
  );
}

function useLocalStorageState<T>(key: string, defaultValue: T): [T, (val: T | ((prev: T) => T)) => void] {
  const [state, setState] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  const setValue = (val: T | ((prev: T) => T)) => {
    try {
      const valueToStore = val instanceof Function ? val(state) : val;
      setState(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch {}
  };

  return [state, setValue];
}

export function AppShell() {
  useAutoPilot();
  const { setCaMintInput } = useApp() as any;
  const isMobile = useIsMobile();
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useLocalStorageState("solclaw-left-panel-collapsed", false);
  const [rightPanelCollapsed, setRightPanelCollapsed] = useLocalStorageState("solclaw-right-panel-collapsed", false);
  const [discoveryPanelCollapsed, setDiscoveryPanelCollapsed] = useLocalStorageState("solclaw-discovery-panel-collapsed", false);
  const { strategistMode, setStrategistMode } = useApp();
  const { t } = useTranslation();

  // ── Mobile layout ──────────────────────────────────────────────────────────
  if (isMobile) {
    return <MobileShell />;
  }

  // ── Desktop layout ─────────────────────────────────────────────────────────
  return (
    <div className="h-dvh w-full flex flex-col bg-[#0b0e11] text-[var(--color-fg)] overflow-hidden font-sans">
      <LiveSignalToasts />
      <PanelGroup
        direction="horizontal"
        className="flex-1"
        autoSaveId="unt-ultrawide-layout-v6"
      >
        {/* COLUMN 1: TOOLS (Full Height) */}
        {!leftPanelCollapsed && (
          <>
            <Panel
              id="tools-column"
              order={1}
              defaultSize={20}
              minSize={15}
              collapsible
              collapsedSize={0}
              className="flex flex-col border-r border-white/5"
            >
              <DashboardSidebar />
              <button
                onClick={() => setLeftPanelCollapsed(true)}
                className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center justify-center rounded-full bg-zinc-900/90 border border-zinc-700 hover:border-emerald-500 hover:bg-emerald-950/30 text-zinc-300 hover:text-emerald-400 shadow-[0_4px_16px_rgba(0,0,0,0.6)] hover:shadow-[0_0_15px_rgba(16,185,129,0.25)] transition-all duration-300 hover:scale-105 w-9 h-9"
                title={t("shell.collapseLeft")}
              >
                <ChevronLeft className="size-4" />
              </button>
            </Panel>
            <ResizeHandle direction="horizontal" />
          </>
        )}

        {/* COLUMN 2: DISCOVERY (Signals + Evidence + Nursery) */}
        {!discoveryPanelCollapsed && (
          <>
             <Panel id="discovery-column" order={2} defaultSize={16} minSize={12} className="relative min-w-0 border-r border-white/5">
                <PanelGroup direction="vertical">
                  <Panel id="signals-top" order={1} defaultSize={50} minSize={20}>
                     <LiveSignalsPanel onOpenToken={(m) => setCaMintInput(m)} />
                  </Panel>
                </PanelGroup>
                <button
                  onClick={() => setDiscoveryPanelCollapsed(true)}
                  className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center justify-center rounded-full bg-zinc-900/90 border border-zinc-700 hover:border-emerald-500 hover:bg-emerald-950/30 text-zinc-300 hover:text-emerald-400 shadow-[0_4px_16px_rgba(0,0,0,0.6)] hover:shadow-[0_0_15px_rgba(16,185,129,0.25)] transition-all duration-300 hover:scale-105 w-9 h-9"
                  title={t("shell.collapseSignals")}
                >
                  <ChevronLeft className="size-4" />
                </button>
             </Panel>
             <ResizeHandle />
          </>
        )}

        {/* COLUMN 3: ANALYSIS (The Matrix) */}
        <Panel id="chart-viewport" order={3} defaultSize={48} minSize={25} className="min-w-0">
          <DashboardViewport />
        </Panel>

        {!rightPanelCollapsed && (
          <>
            <ResizeHandle direction="horizontal" />

            {/* COLUMN 4: CONTEXT (Target Operations Terminal) */}
            <Panel id="context-stack" order={4} defaultSize={16} minSize={12} collapsible collapsedSize={0} className="relative min-w-0 border-l border-white/5">
              {strategistMode ? <ChatPanel /> : <HistoryPanel />}
              <button
                onClick={() => setRightPanelCollapsed(true)}
                className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center justify-center rounded-full bg-zinc-900/90 border border-zinc-700 hover:border-emerald-500 hover:bg-emerald-950/30 text-zinc-300 hover:text-emerald-400 shadow-[0_4px_16px_rgba(0,0,0,0.6)] hover:shadow-[0_0_15px_rgba(16,185,129,0.25)] transition-all duration-300 hover:scale-105 w-9 h-9"
                title={t("shell.collapseRight")}
              >
                <ChevronRight className="size-4" />
              </button>
              <button
                onClick={() => setStrategistMode(!strategistMode)}
                className="absolute top-2 right-4 z-50 flex items-center justify-center rounded-md bg-[var(--color-bg-editor)] border border-[var(--color-border)] hover:border-[var(--color-fg-dim)] hover:bg-[var(--color-fill)] transition-all px-2 py-1 text-[10px] font-bold uppercase tracking-wider"
                title={strategistMode ? t("shell.switchToOps") : t("shell.switchToAI")}
              >
                <Zap className="size-3 mr-1" />
                {strategistMode ? t("shell.agentOps") : "AI"}
              </button>
            </Panel>
          </>
        )}

        {/* Restore buttons when panels are collapsed */}
        {leftPanelCollapsed && (
          <button
            onClick={() => setLeftPanelCollapsed(false)}
            className="absolute bottom-4 left-4 z-50 flex items-center justify-center rounded-full bg-zinc-900/90 border border-zinc-700 hover:border-emerald-500 hover:bg-emerald-950/30 text-zinc-300 hover:text-emerald-400 shadow-[0_4px_16px_rgba(0,0,0,0.6)] hover:shadow-[0_0_15px_rgba(16,185,129,0.25)] transition-all duration-300 hover:scale-105 w-9 h-9"
            title={t("shell.expandLeft")}
          >
            <ChevronRight className="size-4" />
          </button>
        )}

        {discoveryPanelCollapsed && (
          <button
            onClick={() => setDiscoveryPanelCollapsed(false)}
            className="absolute bottom-16 left-4 z-50 flex items-center justify-center rounded-full bg-zinc-900/90 border border-zinc-700 hover:border-emerald-500 hover:bg-emerald-950/30 text-zinc-300 hover:text-emerald-400 shadow-[0_4px_16px_rgba(0,0,0,0.6)] hover:shadow-[0_0_15px_rgba(16,185,129,0.25)] transition-all duration-300 hover:scale-105 w-9 h-9"
            title={t("shell.expandSignals")}
          >
            <Target className="size-4 text-emerald-400" />
          </button>
        )}

        {rightPanelCollapsed && (
          <button
            onClick={() => setRightPanelCollapsed(false)}
            className="absolute bottom-4 right-4 z-50 flex items-center justify-center rounded-full bg-zinc-900/90 border border-zinc-700 hover:border-emerald-500 hover:bg-emerald-950/30 text-zinc-300 hover:text-emerald-400 shadow-[0_4px_16px_rgba(0,0,0,0.6)] hover:shadow-[0_0_15px_rgba(16,185,129,0.25)] transition-all duration-300 hover:scale-105 w-9 h-9"
            title={t("shell.expandRight")}
          >
            <ChevronLeft className="size-4" />
          </button>
        )}
      </PanelGroup>
    </div>
  );
}
